import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { Grid } from '@mui/material';

import BasicDatePicker from '../basic-fields/single-datepicker/BasicDatepicker';

function getDateFromIso(d: string): dayjs.Dayjs {
  return dayjs(d);
}

export interface RangeDatepickerProps {
  control: any;
  reset?: number;
  config?: {
    dateToLabel: string;
    dateFromLabel: string;
    views?: Array<'year' | 'month' | 'day'>;
    disableFutureForFromDate?: boolean;
    disableFutureForToDate?: boolean;
    isFromDateWithCancel?: boolean;
    isToDateWithCancel?: boolean;
    minDate?: dayjs.Dayjs;
    maxDate?: dayjs.Dayjs;
  };
  inputFromName?: string;
  inputToName?: string;
  onDateChange?: (dateFrom: string | null, dateTo: string | null) => void;
}

export default function RangeDatepicker(props: RangeDatepickerProps) {
  const {
    control,
    reset = 0,
    config = {
      dateToLabel: 'Date To',
      dateFromLabel: 'Date From',
      views: ['year', 'month', 'day'],
      disableFutureForFromDate: false,
      disableFutureForToDate: false,
      isFromDateWithCancel: false,
      isToDateWithCancel: false,
      minDate: undefined,
      maxDate: undefined,
    },
    inputFromName = 'dateFrom',
    inputToName = 'dateTo',
    onDateChange = () => {},
  } = props;

  const [shouldDisableDateFrom, setShouldDisableDateFrom] = useState<(date: dayjs.Dayjs) => boolean>();
  const [shouldDisableDateTo, setShouldDisableDateTo] = useState<(date: dayjs.Dayjs) => boolean>();

  const handleDateToChange = (dateTo: string | null) => {
    onDateChange(null, dateTo);
    setShouldDisableDateFrom(() => {
      return (date: dayjs.Dayjs) => {
        if (dateTo && getDateFromIso(dateTo).isBefore(date)) {
          return true;
        }
        return false;
      };
    });
  };

  const handleDateFromChange = (dateFrom: string | null) => {
    onDateChange(dateFrom, null);
    setShouldDisableDateTo(() => {
      return (date: dayjs.Dayjs) => {
        if (dateFrom && date.isBefore(getDateFromIso(dateFrom))) {
          return true;
        }
        return false;
      };
    });
  };

  useEffect(() => {
    if (reset > 0) {
      setTimeout(() => {
        handleDateFromChange(null);
        handleDateToChange(null);
      }, 10);
    }
  }, [reset]);

  useEffect(() => {
    handleDateToChange(control._defaultValues[inputToName]);
    handleDateFromChange(control._defaultValues[inputFromName]);
  }, []);

  return (
    <Grid container columnSpacing={4} spacing={4}>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <BasicDatePicker
          label={config.dateFromLabel}
          inputName={inputFromName}
          control={control}
          shouldDisableDate={shouldDisableDateFrom}
          onChange={handleDateFromChange}
          views={config.views}
          disableFuture={config.disableFutureForFromDate}
          isWithCancel={config.isFromDateWithCancel}
          minDate={config.minDate}
          maxDate={config.maxDate}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <BasicDatePicker
          label={config.dateToLabel}
          inputName={inputToName}
          control={control}
          shouldDisableDate={shouldDisableDateTo}
          onChange={handleDateToChange}
          views={config.views}
          disableFuture={config.disableFutureForToDate}
          isWithCancel={config.isToDateWithCancel}
          minDate={config.minDate}
          maxDate={config.maxDate}
        />
      </Grid>
    </Grid>
  );
}
